// app/components/thailand-premium-tapioca-starch-products/Specification.tsx
"use client";
import { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { BaseRow, baseRows1, baseRows2 } from "./specRows";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#e2c996",
    color: theme.palette.$TCPG900,
    fontSize: 20,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 18,
    borderBottom: 0,
    borderRight: `1px solid ${theme.palette.$TCPB500}`,
    "&:last-child": {
      borderRight: 0,
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.$TCPG300,
    borderBottom: 0,
  },
  "&:last-child td, &:last-child th": {
    borderBottom: 0,
  },
}));

type SpecRowState = {
  label: string;
  data1: string;
  data2: string;
};

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string | null;
};

export default function Specification() {
  const t = useTranslations("OurProducts");
  const locale = useLocale();

  // path ของหน้า (ให้ตรงกับที่ save ใน page-inline)
  const [pagePath, setPagePath] = useState<string>("/thailand-premium-tapioca-starch-products");
  useEffect(() => {
    if (typeof window === "undefined") return;
    setPagePath(window.location.pathname);
  }, []);

  // ---- default text จาก i18n + constant (ดีต่อ SEO / SSR) ----
  const defaultTitle = t("Specification.title");
  const defaultSubtitle = t("Specification.subtitle");
  const defaultTable1Title = t("Specification.Table1.title");
  const defaultTable2Title = t("Specification.Table2.title");

  // helper: สร้าง state rows ตาม locale ตอนนี้
  const makeDefaultRows = (rows: BaseRow[]): SpecRowState[] =>
    rows.map((r) => ({
      label: locale === "zh" ? r.name_zh : r.name,
      data1: r.data1,
      data2: r.data2,
    }));

  // ---- state แสดงจริง (จะถูก inline override ถ้ามี) ----
  const [title, setTitle] = useState(defaultTitle);
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [table1Title, setTable1Title] = useState(defaultTable1Title);
  const [table2Title, setTable2Title] = useState(defaultTable2Title);

  const [rows1, setRows1] = useState<SpecRowState[]>(() =>
    makeDefaultRows(baseRows1)
  );
  const [rows2, setRows2] = useState<SpecRowState[]>(() =>
    makeDefaultRows(baseRows2)
  );

  // ---- locale เปลี่ยน → reset กลับ default ก่อน ----
  useEffect(() => {
    setTitle(defaultTitle);
    setSubtitle(defaultSubtitle);
    setTable1Title(defaultTable1Title);
    setTable2Title(defaultTable2Title);
    setRows1(makeDefaultRows(baseRows1));
    setRows2(makeDefaultRows(baseRows2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

 //
  useEffect(() => {
    if (!pagePath) return;

    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          let key = e.cmsKey ?? undefined;

          // fallback: เผื่อช่วงแรก selector ยังเป็นรูปแบบ [data-cms-key="..."]
          if (!key && e.selector) {
            const match = e.selector.match(/data-cms-key="([^"]+)"/);
            if (match) key = match[1];
          }

          if (key) {
            map[key] = e.value;
          }
        });

        // ---- override header texts ----
        if (map["ourproducts.spec.title"]) {
          setTitle(map["ourproducts.spec.title"]);
        }
        if (map["ourproducts.spec.subtitle"]) {
          setSubtitle(map["ourproducts.spec.subtitle"]);
        }
        if (map["ourproducts.spec.table1.title"]) {
          setTable1Title(map["ourproducts.spec.table1.title"]);
        }
        if (map["ourproducts.spec.table2.title"]) {
          setTable2Title(map["ourproducts.spec.table2.title"]);
        }

        // ---- override rows1 ----
        setRows1((prev) => {
          const next = [...prev];
          for (let i = 0; i < next.length; i++) {
            const idx = i + 1;
            const labelKey = `ourproducts.spec.table1.row${idx}.label`;
            const d1Key = `ourproducts.spec.table1.row${idx}.data1`;
            const d2Key = `ourproducts.spec.table1.row${idx}.data2`;

            if (map[labelKey]) next[i] = { ...next[i], label: map[labelKey] };
            if (map[d1Key]) next[i] = { ...next[i], data1: map[d1Key] };
            if (map[d2Key]) next[i] = { ...next[i], data2: map[d2Key] };
          }
          return next;
        });

        // ---- override rows2 ----
        setRows2((prev) => {
          const next = [...prev];
          for (let i = 0; i < next.length; i++) {
            const idx = i + 1;
            const labelKey = `ourproducts.spec.table2.row${idx}.label`;
            const d1Key = `ourproducts.spec.table2.row${idx}.data1`;
            const d2Key = `ourproducts.spec.table2.row${idx}.data2`;

            if (map[labelKey]) next[i] = { ...next[i], label: map[labelKey] };
            if (map[d1Key]) next[i] = { ...next[i], data1: map[d1Key] };
            if (map[d2Key]) next[i] = { ...next[i], data2: map[d2Key] };
          }
          return next;
        });
      } catch (err) {
        console.warn("[Specification] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box
      className="specification-wrap"
      sx={{
        pt: { xs: 5, md: 10 },
        pb: 5
      }}>
      <Container className="container">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7.5 }} data-aos="fade-up">
            <Typography
              variant="h2"
              data-cms-key="ourproducts.spec.title"
              sx={{
                mb: 2,
                whiteSpace: { xs: "normal", lg: "pre-line" }
              }}>
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{ whiteSpace: { xs: "normal", lg: "pre-line" } }}
              data-cms-key="ourproducts.spec.subtitle"
            >
              {subtitle}
            </Typography>

            {/* Table 1 */}
            <TableContainer component={Paper} sx={{ mt: { xs: 3, md: 3 } }}>
              <Table sx={{ minWidth: 0 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell
                      colSpan={3}
                      align="center"
                      data-cms-key="ourproducts.spec.table1.title"
                    >
                      {table1Title}
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows1.map((row, index) => {
                    const idx = index + 1;
                    return (
                      <StyledTableRow key={`t1-row-${idx}`}>
                        <StyledTableCell
                          component="th"
                          scope="row"
                          data-cms-key={`ourproducts.spec.table1.row${idx}.label`}
                        >
                          {row.label}
                        </StyledTableCell>
                        <StyledTableCell
                          data-cms-key={`ourproducts.spec.table1.row${idx}.data1`}
                        >
                          {row.data1}
                        </StyledTableCell>
                        <StyledTableCell
                          data-cms-key={`ourproducts.spec.table1.row${idx}.data2`}
                        >
                          {row.data2}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Table 2 */}
            <TableContainer component={Paper} sx={{ mt: 5 }}>
              <Table sx={{ minWidth: 0 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell
                      colSpan={3}
                      align="center"
                      data-cms-key="ourproducts.spec.table2.title"
                    >
                      {table2Title}
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows2.map((row, index) => {
                    const idx = index + 1;
                    return (
                      <StyledTableRow key={`t2-row-${idx}`}>
                        <StyledTableCell
                          component="th"
                          scope="row"
                          data-cms-key={`ourproducts.spec.table2.row${idx}.label`}
                        >
                          {row.label}
                        </StyledTableCell>
                        <StyledTableCell
                          data-cms-key={`ourproducts.spec.table2.row${idx}.data1`}
                        >
                          {row.data1}
                        </StyledTableCell>
                        <StyledTableCell
                          data-cms-key={`ourproducts.spec.table2.row${idx}.data2`}
                        >
                          {row.data2}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}